import type { ProjectTemplateSummary } from '@servicetim/shared';
import { Badge } from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { Check, LayoutTemplate } from 'lucide-react';
import type { ReactNode } from 'react';

export interface TemplateCardProps {
  template: ProjectTemplateSummary | { id: 'blank'; name: string; description: string | null };
  /** Selectable card (radio semantics) when given. */
  selected?: boolean;
  onSelect?: () => void;
  /** Radio group name when selectable. */
  radioName?: string;
  /** Footer slot (actions on the template center, nothing on the picker). */
  footer?: ReactNode;
  children?: ReactNode;
}

/** One template (or the blank starter) as a card; selectable in the new-project wizard. */
export function TemplateCard({
  template,
  selected,
  onSelect,
  radioName = 'template',
  footer,
  children,
}: TemplateCardProps) {
  const seeded = 'isSeeded' in template && template.isSeeded;
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="size-4 text-brand-600" aria-hidden="true" />
          <span className="font-medium text-fg">{template.name}</span>
        </div>
        {onSelect ? (
          <span
            aria-hidden="true"
            className={cn(
              'grid size-5 place-items-center rounded-full border',
              selected ? 'border-brand-600 bg-brand-600 text-white' : 'border-border-strong',
            )}
          >
            {selected ? <Check className="size-3.5" /> : null}
          </span>
        ) : seeded ? (
          <Badge variant="secondary">Seeded</Badge>
        ) : (
          <Badge variant="outline">Saved</Badge>
        )}
      </div>
      <p className="text-fg-muted text-sm">{template.description ?? 'No description.'}</p>
      {children}
    </>
  );
  if (onSelect) {
    return (
      <label
        className={cn(
          'flex h-full cursor-pointer flex-col gap-2 rounded-lg border bg-surface-raised p-4 text-left shadow-xs transition-colors',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40',
          selected ? 'border-accent ring-1 ring-accent' : 'border-border hover:bg-surface-sunken',
        )}
      >
        <input
          type="radio"
          name={radioName}
          className="sr-only"
          checked={selected ?? false}
          onChange={onSelect}
          aria-label={template.name}
        />
        {body}
      </label>
    );
  }
  return (
    <div className="flex h-full flex-col gap-2 rounded-lg border border-border bg-surface-raised p-4 shadow-xs">
      {body}
      {footer ? (
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">{footer}</div>
      ) : null}
    </div>
  );
}
